import { Badge, Box, Flex, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue } from '@chakra-ui/react';

import {
    createColumnHelper,
    CellContext,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';

// Custom components
import Card from 'components/card/Card';
import SortIndicator from 'components/table/SortIndicator';
import { useMemo, useState } from 'react';
import { OrganizationInvitationDto } from '../../../models/organization/dto/organization.dto';
import { translate } from '../../../i18n';
import { dateToString } from '../../../utils/date';
import { useGetSelectedOrganizationView } from '../hooks/useGetCurrentOrganization';
import { useSelectedOrganization } from '../hooks/useSelectedOrganization';
import { useGetPendingInvitations } from '../hooks/useGetPendingInvitations';
import ErrorState from '../../../components/states/ErrorState';
import { typography } from 'theme/tokens';
import { tableProps, tableThProps, tableThFlexProps, tableCellProps, tableHeaderTextProps } from 'theme/constants';


const columnHelper = createColumnHelper<OrganizationInvitationDto>();

export default function OrganizationPendingInvitationList() {
    const selectedOrganization = useSelectedOrganization.use.selectedOrganization();
    const { data, isError } = useGetSelectedOrganizationView(selectedOrganization?.id);
    const { data: pendingInvitations } = useGetPendingInvitations(data?.organization?.id);
    const [sorting, setSorting] = useState<SortingState>([]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const columns = useMemo(() => [
        columnHelper.accessor('email', {
            id: 'email',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('organization.email')}
                </Text>
            ),
            cell: (info: CellContext<OrganizationInvitationDto, string>) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('organization.sent_at')}
                </Text>
            ),
            cell: (info: CellContext<OrganizationInvitationDto, string>) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {dateToString(new Date(info.getValue()))}
                    </Text>
                </Flex>
            )
        }),
        // Invitations now expire (the API refuses an accept past the TTL). An
        // expired one still sits in this list looking identical to a fresh
        // one, so without this column an admin would watch an invitee fail to
        // accept with no way to see why — the fix is to revoke and re-invite,
        // and that is only discoverable if the state is visible.
        columnHelper.accessor('isExpired', {
            id: 'isExpired',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('organization.invitation_status')}
                </Text>
            ),
            cell: (info: CellContext<OrganizationInvitationDto, boolean>) => (
                <Flex align='center'>
                    <Badge colorScheme={info.getValue() ? 'red' : 'green'} fontSize='xs'>
                        {translate(info.getValue()
                            ? 'organization.invitation_expired'
                            : 'organization.invitation_pending')}
                    </Badge>
                </Flex>
            )
        })
    ], [textColor]);
    const table = useReactTable({
        data: pendingInvitations || [],
        columns,
        state: {
            sorting
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (isError) {
        return (
            <Card flexDirection='column' w='100%' px='0px'>
                <ErrorState message={translate('common.error')} />
            </Card>
        );
    }

    return (
        <Card flexDirection='column' w='100%' px='0px' overflowX={{ sm: 'scroll', lg: 'hidden' }}>
            <Flex px='25px' mb="8px" justifyContent='space-between' align='center'>
                <Text color={textColor} {...typography.pageTitle}>
                    {translate('organization.invitations')}
                </Text>
            </Flex>
            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <Th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            {...tableThProps}
                                            borderColor={borderColor}
                                            cursor='pointer'
                                            onClick={header.column.getToggleSortingHandler()}>
                                            <Flex
                                                {...tableThFlexProps}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                <SortIndicator direction={header.column.getIsSorted()} />
                                            </Flex>
                                        </Th>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <Tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <Td
                                                key={cell.id}
                                                {...tableCellProps}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </Box>
        </Card>
    );
}
